import axios from "axios";
import { smartsightAPI } from "../constants/defaultValues";
import jwt from "jsonwebtoken";
import store from "../redux/store";
import { LOGOUT_USER, PASSWORD_EXPIRED } from "../redux/actions";
import { VIEW_ONLY_ERROR, clearStoredViewOnly } from "./accessControl";
import { NotificationManager } from "../components/common/react-notifications";

const apiAuth = axios.create({
  baseURL: smartsightAPI,
});

// Tokens live in localStorage, with sessionStorage as a fallback (the
// workspace view writes both).
const readStoredToken = (key) =>
  localStorage.getItem(key) || sessionStorage.getItem(key);

// Error for a request that needs a session when there is none. It carries a
// 401 response so existing `error.response.status === 401` handlers treat it
// as "logged out" — no request is actually sent.
const notAuthenticatedError = () => {
  const error = new Error("Not authenticated");
  error.isNotAuthenticated = true;
  error.response = { status: 401, data: { detail: "Not authenticated" } };
  return error;
};
// The session cannot be recovered (no refresh token, or the backend rejected
// it): drop the stored tokens and log out so the user lands on the login page
// instead of every later request retrying a dead refresh.
const endSession = () => {
  ["jwt", "jwtRefresh"].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  clearStoredViewOnly();
  if (store.getState().authUser.user) {
    store.dispatch({ type: LOGOUT_USER });
  }
};

const requestRefresh = async () => {
  const refreshToken = readStoredToken("jwtRefresh");
  if (!refreshToken) {
    endSession();
    throw notAuthenticatedError();
  }

  let res;
  try {
    res = await axios.post(smartsightAPI + "/api/token/refresh/", {
      refresh: refreshToken,
    });
  } catch (error) {
    // 400/401 mean the refresh token itself is bad or expired. Anything else
    // (429, 5xx, network) is transient, so keep the session.
    const status = error.response?.status;
    if (status === 400 || status === 401) endSession();
    throw error;
  }

  // Store new access token in both locations
  localStorage.setItem("jwt", res.data.access);
  sessionStorage.setItem("jwt", res.data.access);
  return res.data.access;
};

// One refresh at a time: requests that find the token expired while a refresh
// is already running wait for it instead of each POSTing their own.
let refreshInFlight = null;

export const refreshAccessToken = () => {
  if (!refreshInFlight) {
    refreshInFlight = requestRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

const requestHandler = async (request) => {
  const token = readStoredToken("jwt");
  const decodedToken = jwt.decode(token, { complete: true });
  const now = new Date().getTime() / 1000;
  const isExpired = decodedToken === null || decodedToken.payload.exp < now;

  if (isExpired) {
    const access = await refreshAccessToken();
    request.headers["Authorization"] = "Bearer " + access;
  } else {
    request.headers["Authorization"] = "Bearer " + token;
  }
  return request;
};

apiAuth.interceptors.request.use(async (request) => requestHandler(request));

// Response interceptor to catch 403 PASSWORD_EXPIRED from middleware
apiAuth.interceptors.response.use(
  (response) => response,
  (error) => {
    // View-only users: any write is blocked server-side with
    // 403 VIEW_ONLY_ACCESS. Show a clear message and keep the promise rejected
    // so calling code does not treat it as a success. Match on the `error`
    // field, not the 403 status alone — 403 is also used for password expiry
    // and insufficient permissions, which have their own handling.
    if (
      error.response?.status === 403 &&
      error.response?.data?.error === VIEW_ONLY_ERROR
    ) {
      const message =
        error.response?.data?.message ||
        "Your account has view-only access. You are not allowed to create, update or delete data.";
      NotificationManager.warning(message, "View-only access", 4000, null, null, "");
      return Promise.reject(error);
    }

    if (
      error.response?.status === 403 &&
      error.response?.data?.error === "PASSWORD_EXPIRED"
    ) {
      // Force show the password reset modal
      store.dispatch({
        type: PASSWORD_EXPIRED,
        payload: {
          reason: error.response.data.force_reset ? "admin" : "expired",
          daysRemaining: 0,
        },
      });

      // Reject with a specific flag so calling code knows not to show generic errors
      const passwordExpiredError = new Error("PASSWORD_EXPIRED");
      passwordExpiredError.isPasswordExpired = true;
      return Promise.reject(passwordExpiredError);
    }

    return Promise.reject(error);
  }
);

export default apiAuth;
